import React, { useState, useCallback, useMemo } from 'react';

import {
  BoldIcon,
  ItalicIcon,
  LinkIcon,
  LinkSlashIcon,
  ListBulletIcon,
  NumberedListIcon,
  UnderlineIcon,
} from '@ergeon/icons/hero/micro';

import { Divider } from './components/Divider';
import { FormatButton } from './components/FormatButton';
import { LinkDialog } from './components/LinkDialog';
import { DEFAULT_TOOLBAR_CONFIG } from './constants';
import { useEditor, usePasteHandler, useRichTextClasses, useToolbar } from './hooks';
import type { FormatState, RichTextInputProps, ToolbarConfig } from './types';

const isMac = typeof navigator !== 'undefined' && (/Mac|iPhone|iPad/).test(navigator.userAgent);
const MOD_LABEL = isMac ? '\u2318' : 'Ctrl+';

type FormatButtonDef = {
  key: keyof ToolbarConfig;
  command: string;
  formatKey: keyof FormatState;
  Icon: React.FC<{ className?: string }>;
  label: string;
  shortcutKey?: string;
  iconClass: string;
};

const FORMAT_BUTTONS: FormatButtonDef[] = [
  { key: 'bold', command: 'bold', formatKey: 'bold', Icon: BoldIcon, label: 'Bold', shortcutKey: 'B', iconClass: 'size-4' },
  { key: 'italic', command: 'italic', formatKey: 'italic', Icon: ItalicIcon, label: 'Italic', shortcutKey: 'I', iconClass: 'size-4' },
  { key: 'underline', command: 'underline', formatKey: 'underline', Icon: UnderlineIcon, label: 'Underline', shortcutKey: 'U', iconClass: 'size-5' },
];

const LIST_BUTTONS: FormatButtonDef[] = [
  { key: 'bulletList', command: 'insertUnorderedList', formatKey: 'unorderedList', Icon: ListBulletIcon, label: 'Bullet List', iconClass: 'size-5' },
  { key: 'orderedList', command: 'insertOrderedList', formatKey: 'orderedList', Icon: NumberedListIcon, label: 'Numbered List', iconClass: 'size-4' },
];

const KEYBOARD_SHORTCUTS: Record<string, string> = Object.fromEntries(
  FORMAT_BUTTONS.filter((btn) => btn.shortcutKey).map((btn) => [btn.shortcutKey!.toLowerCase(), btn.command]),
);

export const RichTextInput: React.FC<RichTextInputProps> = ({
  label,
  placeholder,
  value,
  onChange,
  onKeyDown,
  isValid,
  validationMessage,
  isDisabled = false,
  minRows = 3,
  toolbar = DEFAULT_TOOLBAR_CONFIG,
  onFilePaste,
  className,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectedUrl, setSelectedUrl] = useState('');

  const hasContent = useMemo(
    () => Boolean(value && value !== '<br>' && value.replace(/<[^>]*>/g, '').trim()),
    [value],
  );

  const {
    editorRef,
    handleInput,
    findLinkElement,
    applyFormat,
    saveSelection,
    restoreSelection,
    insertLink,
    insertHTML,
  } = useEditor({ value, onChange });

  const isSelectionInLink = useCallback(() => findLinkElement() !== null, [findLinkElement]);

  const {
    formatState,
    updateFormatState,
    handleSelectionChange,
  } = useToolbar({ editorRef, isSelectionInLink });

  const classes = useRichTextClasses({ isDisabled, isValid, isFocused, hasValue: hasContent, className });

  const { handlePaste } = usePasteHandler({
    onPaste: insertHTML,
    onFilePaste,
  });

  const handleFormat = useCallback((command: string) => {
    applyFormat(command);
    setTimeout(updateFormatState, 10);
  }, [applyFormat, updateFormatState]);

  // Link dialog handlers
  const handleLinkClick = useCallback(() => {
    const text = saveSelection();
    const existingLink = findLinkElement();
    setSelectedText(existingLink?.textContent || text);
    setSelectedUrl(existingLink?.href || '');
    setShowLinkDialog(true);
  }, [saveSelection, findLinkElement]);

  const handleLinkSave = useCallback((text: string, url: string) => {
    insertLink(text, url);
    setShowLinkDialog(false);
    setSelectedText('');
    setSelectedUrl('');
  }, [insertLink]);

  const handleLinkCancel = useCallback(() => {
    setShowLinkDialog(false);
    setSelectedText('');
    setSelectedUrl('');
    restoreSelection();
    editorRef.current?.focus();
  }, [restoreSelection, editorRef]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const isMod = event.metaKey || event.ctrlKey;

    if (isMod && event.key === 'Enter') {
      event.preventDefault();
      onKeyDown?.({ key: 'Enter', metaKey: true } as React.KeyboardEvent);
      return;
    }

    if (isMod) {
      const key = event.key.toLowerCase();
      if (key === 'k') {
        event.preventDefault();
        handleLinkClick();
      } else if (KEYBOARD_SHORTCUTS[key]) {
        event.preventDefault();
        handleFormat(KEYBOARD_SHORTCUTS[key]);
      }
    }

    onKeyDown?.(event);
  }, [onKeyDown, handleFormat, handleLinkClick]);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!document.activeElement?.closest('[data-editor-toolbar]')) {
        setIsFocused(false);
      }
    }, 100);
  }, []);

  const editorStyle = useMemo(() => ({
    minHeight: `${minRows * 24}px`,
    maxHeight: `${Math.max(minRows * 2, 8) * 24}px`,
  }), [minRows]);

  const hasListButtons = LIST_BUTTONS.some((btn) => toolbar[btn.key]);

  return (
    <div className={classes.containerClasses}>
      <div className={classes.editorWrapperClasses}>
        <div className="relative">
          {label && <label className={classes.labelClasses}>{label}</label>}

          {!showLinkDialog && !isDisabled && (isFocused || hasContent) && (
            <div className={`flex items-center gap-1 px-3 pt-2 pb-1 transition-opacity ${isFocused ? 'opacity-100' : 'opacity-30'}`} data-editor-toolbar>
              {FORMAT_BUTTONS.map((btn) => toolbar[btn.key] && (
                <FormatButton
                  active={formatState[btn.formatKey]}
                  key={btn.key}
                  onClick={() => handleFormat(btn.command)}
                  title={btn.shortcutKey ? `${btn.label} (${MOD_LABEL}${btn.shortcutKey})` : btn.label}>
                  <btn.Icon className={btn.iconClass} />
                </FormatButton>
              ))}

              {toolbar.link && <Divider />}
              {toolbar.link && (
                <FormatButton active={formatState.link} onClick={handleLinkClick} title={`Link (${MOD_LABEL}K)`}>
                  <LinkIcon className="size-4" />
                </FormatButton>
              )}
              {toolbar.link && formatState.link && (
                <FormatButton onClick={() => handleFormat('unlink')} title="Remove Link">
                  <LinkSlashIcon className="size-4" />
                </FormatButton>
              )}

              {hasListButtons && <Divider />}
              {LIST_BUTTONS.map((btn) => toolbar[btn.key] && (
                <FormatButton
                  active={formatState[btn.formatKey]}
                  key={btn.key}
                  onClick={() => handleFormat(btn.command)}
                  title={btn.label}>
                  <btn.Icon className={btn.iconClass} />
                </FormatButton>
              ))}
            </div>
          )}

          <div className="relative">
            {!hasContent && isFocused && placeholder && (
              <div className="top-3 left-3 absolute text-gray-400 dark:text-gray-500 pointer-events-none paragraph-2">
                {placeholder}
              </div>
            )}

            <div
              className={classes.textareaClasses}
              contentEditable={!isDisabled}
              onBlur={handleBlur}
              onFocus={() => setIsFocused(true)}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onKeyUp={handleSelectionChange}
              onMouseUp={handleSelectionChange}
              onPaste={handlePaste}
              ref={editorRef}
              role="textbox"
              style={editorStyle}
              suppressContentEditableWarning />
          </div>
        </div>
      </div>

      <LinkDialog
        initialText={selectedText}
        initialUrl={selectedUrl}
        isVisible={showLinkDialog}
        onCancel={handleLinkCancel}
        onSave={handleLinkSave} />

      {validationMessage && isValid === false && (
        <span className={classes.validationMessageClasses}>{validationMessage}</span>
      )}
    </div>
  );
};

export default RichTextInput;
